import { ReactNode } from 'react';
import {
  EmploymentType,
  Gender,
  RetirementCalculatorField,
  RetirementCalculatorStep,
  SavingsField,
  SavingsProduct,
} from './RetirementCalculator.const';

export interface RetirementCalculatorFormValuesModel {
  [RetirementCalculatorField.MONTHLY_GROSS_SALARY]?: number;
  [RetirementCalculatorField.EMPLOYMENT_TYPE]?: EmploymentType;
  [RetirementCalculatorField.BIRTH_YEAR]?: number;
  [RetirementCalculatorField.GENDER]?: Gender;
}

export type RetirementCalculatorSubmitValuesModel = Required<RetirementCalculatorFormValuesModel>;

export enum RetirementCalculatorErrorCode {
  REQUIRED = 'required',
  SALARY_BELOW_MIN = 'salaryBelowMin',
  AGE_OUT_OF_RANGE = 'ageOutOfRange',
}

export type RetirementCalculatorErrorCodesModel = Partial<
  Record<RetirementCalculatorField, RetirementCalculatorErrorCode>
>;

export type RetirementCalculatorErrorsModel = Partial<
  Record<RetirementCalculatorField, string>
>;

export interface SavingsProductValueModel {
  enabled: boolean;
  [SavingsField.ACCUMULATED]?: number;
  [SavingsField.MONTHLY_CONTRIBUTION]?: number;
}

export type SavingsValuesModel = Record<SavingsProduct, SavingsProductValueModel>;

export type YearlyContributionLimitsModel = Partial<Record<SavingsProduct, number>>;

export type ContributionLimitsFallbackModel = YearlyContributionLimitsModel & {
  ikzeEntrepreneur?: number;
};

export interface ContributionLimitsSourceModel {
  disabled?: boolean;
  baseUrl?: string;
  fallbackLimits?: ContributionLimitsFallbackModel;
}

export interface RetirementCalculatorSettingsModel {
  retirementAge: Record<Gender, number>;
  payoutYears: number;
  averageReturnRate: number;
  yearlyContributionLimits: YearlyContributionLimitsModel;
}

export interface ZusPensionModel {
  monthlyPension: number;
  accumulatedCapital: number;
  futureContributions: number;
  totalCapital: number;
  monthsToRetirement: number;
  payoutMonths: number;
}

export interface RetirementProjectionModel {
  monthlyPension: number;
  zusPension?: ZusPensionModel;
  salaryReplacementRate?: number;
  savingsMonthlyPayout?: number;
  totalMonthlyPayout?: number;
  savingsBreakdown?: SavingsPayoutBreakdownModel;
  promotedProductPayout?: number;
}

export type SavingsPayoutBreakdownModel = Partial<Record<SavingsProduct, number>>;

export interface RetirementCalculatorSettingsOverridesModel {
  averageReturnRate?: number;
  retirementAge?: number;
  payoutYears?: number;
}

interface RetirementCalculatorSummaryBasePropsModel {
  basicData?: RetirementCalculatorFormValuesModel;
  savings?: SavingsValuesModel;
  settings: RetirementCalculatorSettingsModel;
  settingsOverrides?: RetirementCalculatorSettingsOverridesModel;
  onSettingsOverridesChange?: (overrides: RetirementCalculatorSettingsOverridesModel) => void;
  onEdit?: (step: RetirementCalculatorStep) => void;
}

export interface RetirementCalculatorSummaryPropsModel
  extends RetirementCalculatorSummaryBasePropsModel {
  className?: string;
}

export interface RetirementCalculatorOutcomePropsModel {
  projection?: RetirementProjectionModel;
  promotedProduct: SavingsProduct.IKE | SavingsProduct.IKZE;
  hasIke?: boolean;
  payoutYears: number;
  monthlyInvestment: number;
  maxMonthlyInvestment: number;
  onMonthlyInvestmentChange: (value: number) => void;
  employmentType?: EmploymentType;
  promoTitle?: ReactNode;
  promoDescription?: ReactNode;
  learnMoreLabel?: string;
  learnMoreUrl?: string;
  onDownloadSummary?: () => void;
  className?: string;
}

export interface RetirementCalculatorLimitsPropsModel {
  projection?: RetirementProjectionModel;
  year?: number;
  className?: string;
}

export interface RetirementCalculatorTextsModel {
  title?: ReactNode;
  submitLabel?: string;
  savingsSubmitLabel?: string;
  salaryTooltip?: ReactNode;
  promoTitle?: ReactNode;
  promoDescription?: ReactNode;
  learnMoreLabel?: string;
}

export interface RetirementCalculatorPropsModel {
  texts?: RetirementCalculatorTextsModel;
  initialValues?: RetirementCalculatorFormValuesModel;
  settings?: Partial<RetirementCalculatorSettingsModel>;
  limitsSource?: ContributionLimitsSourceModel;
  learnMoreUrl?: string;
  onDownloadSummary?: () => void;
  onBasicDataSubmit?: (values: RetirementCalculatorSubmitValuesModel) => void;
  onSavingsSubmit?: (
    values: SavingsValuesModel,
    projection?: RetirementProjectionModel,
  ) => void;
  onStepChange?: (step: RetirementCalculatorStep) => void;
  disabled?: boolean;
  isLoading?: boolean;
  component?: 'main' | 'section' | 'div';
  className?: string;
  testId?: string;
}

export interface RetirementCalculatorFormPropsModel
  extends Pick<
    RetirementCalculatorPropsModel,
    'initialValues' | 'disabled' | 'isLoading'
  >,
    Pick<RetirementCalculatorTextsModel, 'title' | 'submitLabel' | 'salaryTooltip'> {
  titleId: string;
  onSubmit?: (values: RetirementCalculatorSubmitValuesModel) => void;
  className?: string;
}

export interface RetirementCalculatorSavingsPropsModel
  extends Pick<RetirementCalculatorPropsModel, 'disabled' | 'isLoading'>,
    Pick<RetirementCalculatorTextsModel, 'title'> {
  titleId: string;
  submitLabel?: string;
  initialValues?: SavingsValuesModel;
  yearlyContributionLimits?: YearlyContributionLimitsModel;
  onSubmit?: (values: SavingsValuesModel) => void;
  onValuesChange?: (values: SavingsValuesModel) => void;
  className?: string;
}

export interface RetirementCalculatorLayoutContextModel
  extends Pick<RetirementCalculatorPropsModel, 'component' | 'className' | 'testId'> {
  titleId: string;
  step: RetirementCalculatorStep;
  onBack?: () => void;
  autoFocus?: boolean;
}

export interface RetirementCalculatorBasicDataStepPropsModel
  extends Omit<RetirementCalculatorFormPropsModel, 'className' | 'titleId'> {}

export interface RetirementCalculatorSavingsStepPropsModel
  extends Omit<RetirementCalculatorSavingsPropsModel, 'className' | 'titleId'> {
  projection?: RetirementProjectionModel;
}

export interface RetirementCalculatorOutcomeStepPropsModel
  extends RetirementCalculatorSummaryBasePropsModel,
    Pick<
      RetirementCalculatorOutcomePropsModel,
      | 'payoutYears'
      | 'monthlyInvestment'
      | 'maxMonthlyInvestment'
      | 'onMonthlyInvestmentChange'
      | 'learnMoreLabel'
      | 'learnMoreUrl'
      | 'onDownloadSummary'
    > {
  projection?: RetirementProjectionModel;
  promotedProduct?: SavingsProduct.IKE | SavingsProduct.IKZE;
  promoTitle?: ReactNode;
  promoDescription?: ReactNode;
}

export interface RetirementCalculatorResultPropsModel {
  projection?: RetirementProjectionModel;
  className?: string;
}

export interface RetirementChartSeriesModel {
  key: string;
  name: string;
  color: string;
  values: Array<number | null>;
}

export interface RetirementCalculatorChartPropsModel {
  series: RetirementChartSeriesModel[];
  barCaptions?: string[];
  highlightedBar?: number;
  barsCount: number;
  scaleTicks: number[];
  description?: string;
  className?: string;
}

export interface RetirementCalculatorChartCardPropsModel {
  series: RetirementChartSeriesModel[];
  scaleTicks: number[];
  barsCount?: number;
  barCaptions?: string[];
  highlightedBar?: number;
  description?: string;
  testId?: string;
}

export interface RetirementCalculatorPromoCardPropsModel {
  variant?: 'wide' | 'compact';
  badge: ReactNode;
  title: ReactNode;
  description: ReactNode;
  descriptionClassName?: string;
  testId?: string;
}
